/*!
 * Copyright 2022 Thorne Health Lab. All rights reserved.
 */
// eslint-disable-next-line
import {
  cloneDeep, flatten,
  get, set, isString,
  isNull, has, includes,
  map, min as minimun,
  max as maximum, clamp, find, round,
  first, each, isUndefined
} from 'lodash';
import fp from 'lodash/fp';
import moment from 'moment-timezone';

import diff from './diff';
import defaultAssessments from '../config/assessments';
import { reportSubitems } from '../config/app';

export const generateUUID = () => {
  let d = new Date().getTime();
  if (
    typeof window.performance !== 'undefined' &&
    typeof window.performance.now === 'function'
  ) {
    d += window.performance.now();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

export const humanizeSnakeCase = str => {
  const frags = str.split('_');
  frags.map((item, index) => {
    frags[index] = frags[index].charAt(0).toUpperCase() + frags[index].slice(1);
    return false;
  });
  return frags.join(' ');
};

export const camelize = str => {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, function (letter, index) {
      return index === 0 ? letter.toLowerCase() : letter.toUpperCase();
    })
    .replace(/\s+/g, '');
};

export const titleCase = str => {
  return str.toLowerCase().replace(/(?:^|[\s-/])\w/g, function (match) {
    return match.toUpperCase();
  });
};

/**
 * Method is used to perform deep compare of objects and replacing the default values with latest from source.
 * @param {*} target
 * @param {*} source
 * @returns
 */
const mergeDeep = (target, source) => {
  try {
    const isDeep = prop =>
      isObject(source[prop]) &&
      target.hasOwnProperty(prop) &&
      isObject(target[prop]);
    const replaced = Object.getOwnPropertyNames(source)
      .map(prop => ({
        [prop]: isDeep(prop)
          ? mergeDeep(target[prop], source[prop])
          : source[prop]
      }))
      .reduce((a, b) => ({ ...a, ...b }), {});

    return {
      ...target,
      ...replaced
    };
  } catch (e) {
    // TODO: cross-check with the funationality team if the source object is corrupted, what could be the course of action.
    console.log('Error performing deep merge', e);
    return target;
  }
};

export const addTitleToReports = fp.pipe([
  fp.toPairs,
  fp.map(pair => [
    pair[0],
    {
      ...pair[1],
      _meta: {
        title: humanizeSnakeCase(pair[0]),
        originalTitle: pair[0],
        icon: reportSubitems[camelize(humanizeSnakeCase(pair[0]))]
          ? reportSubitems[camelize(humanizeSnakeCase(pair[0]))].icon
          : null
      }
    }
  ]),
  fp.fromPairs
]);

export const assert = {
  isString: item => isString(item),
  isDate: item => moment(item).isValid()
};

export const extendAssessments = (input = {}) => {
  return mergeDeep(cloneDeep(defaultAssessments), input);
};

export const cleanAssessments = (input = {}) => {
  return diff(defaultAssessments, input);
};

export const formatVisit = visit => {
  const { uuid, date } = visit;
  const estTime = moment(date).tz('America/New_York');
  const formattedDate = moment(estTime).format('MMMM Do, YYYY');
  const dayFormattedDate = moment(estTime).format('dddd, MMMM Do, YYYY');
  const longFormattedDate = moment(estTime).format('dddd, MMMM Do [at] h:mm a');
  const title = `Visit on ${formattedDate}`;
  const subTitle = 'Hosted at Lab100 Beta Clinic, Mount Sinai, NY';
  const route = `/visits/${uuid}`;

  return {
    formattedDate,
    dayFormattedDate,
    longFormattedDate,
    title,
    subTitle,
    route
  };
};

// Filter missed appointments.
export function filterMissedVisits(rawVisits) {
  let visits = [];
  if (rawVisits && rawVisits.length > 0) {
    visits = rawVisits.filter(visit => {
      const currentTime = moment().utc();
      const visitTime = moment(visit.date)
        .utc()
        .add('60', 'minutes');
      const isUpcoming = currentTime.isBefore(visitTime);

      return !(!isUpcoming && !visit.status.completed);
    });
  }
  return visits;
}

export const getTimeWithAmOrPm = time => {
  if (!time) return '';
  const parsedTime = parseInt(time?.split(':')[0]);
  if (parsedTime === 12) {
    return `${time} PM`;
  }
  if (parsedTime === 0) {
    return `12:${time?.split(':')[1]} AM`;
  }
  return parsedTime > 12
    ? `${parsedTime - 12}:${time.split(':')[1]} PM`
    : `${time} AM`;
};

export const normalizeData = org => {
  const data = cloneDeep(org);
  const id = data.__id || '??';
  let ranges = [];
  if (has(data, 'distributionStats')) {
    if (includes(data.__path, 'lipids')) {
      ranges = lipidRanges(data.distributionStats);
    } else {
      ranges = distRanges(data.distributionStats);
    }
  } else {
    // Catch state for the 'default data'
    ranges = data.ranges;
  }

  const candidates = flatten(
    map(ranges, range => [range.min, range.max])
  );
  const min = minimun(candidates);
  const max = maximum(candidates);
  const range = find(ranges, ['interpretation', data.interpretation]) ||
    find(
      ranges,
      range => range.min <= data.value && data.value < range.max
    ) ||
    first(ranges);

  const color = get(range, 'color', '#575757');
  const value = round(clamp(data.value, min, max), 2);
  return {
    id: id,
    ranges: ranges,
    selected: range,
    interpretation: range.interpretation || 'N/A',
    min: min,
    max: max,
    color: color,
    value: value,
    unit: data.unit
  };

  function lipidRanges(stats) {
    if (data.__path === 'laboratory.lipids.cholesterol') {
      // console.log(id, data.ranges)
      if (has(data, 'ranges[0].min')) {
        data.ranges[0].min = 200 - 13.33 - 40.235797202411455;
      }
      if (has(data, 'ranges[2].max')) {
        data.ranges[2].max = 240 + 13.33 + 40.235797202411455;
      }
    }
    if (data.__path === 'laboratory.lipids.hdl') {
      // console.log(id, data.ranges)
      if (has(data, 'ranges[0].min')) {
        data.ranges[0].min = 40 - 6.6666 - 12.497959445448435;
      }
      if (has(data, 'ranges[2].max')) {
        data.ranges[2].max = 60 + 6.6666 + 12.497959445448435;
      }
    }
    if (data.__path === 'laboratory.lipids.cholesterol-hdl') {
      // console.log(id, data.ranges)
      if (has(data, 'ranges[0].min')) {
        data.ranges[0].min = 0;
      }
      if (has(data, 'ranges[1].max')) {
        data.ranges[1].max = 10;
      }
    }
    if (data.__path === 'laboratory.lipids.ldl') {
      // console.log(id, data)

      if (has(data, 'ranges[0].min')) {
        // data.ranges[0].min = 0
      }
      if (has(data, 'ranges[4].max')) {
        data.ranges[4].max = 260;
      }
    }
    if (data.__path === 'laboratory.lipids.trig') {
      // console.log(id, data.ranges)
      if (has(data, 'ranges[3].max')) {
        data.ranges[3].max = 550;
      }
    }
    if (data.__path === 'laboratory.lipids.vldl') {
      // console.log(id, data.ranges)
      if (has(data, 'ranges[0].min')) {
        data.ranges[0].min = 0;
      }
      if (has(data, 'ranges[1].max')) {
        data.ranges[1].max = 60;
      }
    }
    return data.ranges;
  }

  function distRanges(stats, int = 'Normal') {
    if (stats.sd === 0) {
      console.error(`${id} - NOT ENOUGH PARAMETERSS [Standard deviation]`);
      each(data.ranges, range => set(range, 'color', 'red'));
      return data.ranges; // Catch empty sd
    }
    const baseRange = find(data.ranges, ['interpretation', 'Normal']);

    if (!baseRange) {
      console.error(id, data.ranges);
    } else {
      const halfrange = (baseRange.max - baseRange.min) / 3;

      let min = baseRange.min - halfrange - stats.sd;
      const max = baseRange.max + halfrange + stats.sd;
      if (min < 0) min = 0;

      const ranges = [
        {
          interpretation: 'Low',
          color: '#ff3548',
          min: min,
          max: baseRange.min
        },
        baseRange,
        {
          interpretation: 'High',
          color: '#ff3548',
          min: baseRange.max,
          max: max
        }
      ];
      return ranges;
    }

    return data.ranges;
  }
};

const isObject = item => typeof item === 'object' && !Array.isArray(item);

/**
 * Helper method to check value is null or undefined
 * @param {*} value
 * @returns
 */
export const isNullOrUndefined = value => {
  // TODO: Can extend to check for empty values
  return !isNull(value) && !isUndefined(value);
};
